import React, { ReactNode, useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Badge, Icon, IconProps, LinkAsButton, RadioCard,
} from '@printdeal/ui-library';
import clsx from 'clsx';
import { Price, SupportArticleModal, VatModeContext } from '@printdeal/product-selector';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import { Money } from '../../Money';

interface Props {
  active: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  icon: IconProps['icon'];
  price?: Price;
  priceLoading?: boolean;
  free?: boolean;
  title: string;
  children?: ReactNode | ReactNode[];
  value?: string;
  labelText?: string;
  supportArticleId?: string;
}

export const DeliveryToolRadioCard = ({
  active,
  onChange = () => {},
  icon,
  free,
  children,
  title,
  value,
  price,
  priceLoading,
  labelText,
  supportArticleId,
}:
Props) => {
  const { vatMode } = useContext(VatModeContext);
  const showPrice = price && !priceLoading;
  const [showSupportArticleModal, setShowSupportArticleModal] = useState(false);
  const intl = useIntl();

  const toggleSupportArticleModal = (open: boolean) => {
    setShowSupportArticleModal(open);
    addDataLayerEntry(`support-article-${open ? 'open' : 'close'}`, { supportArticleId });
  };

  return (
    <>
      {supportArticleId && (
      <SupportArticleModal
        supportArticleId={supportArticleId}
        show={showSupportArticleModal}
        onClose={() => toggleSupportArticleModal(false)}
      />
      )}
      <RadioCard
        id={title}
        label={title}
        name={title}
        value={value}
        image={<Icon icon={icon} size="xl" className={clsx({ 'pd-text-primary-500': active })} />}
        checked={active}
        onChange={onChange}
        description={(
          <div className="pd-font-normal">
            <div className="pd-mb-2 pd-flex pd-items-center pd-gap-2 pd-align-middle">
              {free && <FormattedMessage id="delivery.tool.price.free" defaultMessage="Free" />}

              {showPrice && <Money value={price[vatMode].centAmount} currency={price.totalNet.currencyCode} />}

              {priceLoading && <span className="pd-animate-pulse">-,--</span>}

              {labelText && (
              <Badge variant="primary-outline">
                {labelText}
              </Badge>
              )}

              {supportArticleId && (
              <LinkAsButton
                variant="secondary"
                onClick={() => toggleSupportArticleModal(true)}
                iconBefore="info"
                screenReaderLabel={intl.formatMessage({ id: 'service.filecheck.secondaryAction.screenReaderLabel', defaultMessage: 'More Information' })}
              />
              )}
            </div>
            {children}
          </div>
        )}
      />
    </>
  );
};
